import { useEffect } from 'react';
import { useMutation } from 'react-query';

import { Button, useNotify, Text, Modal } from '@hotjar/hotsauce';
import { useOrganizationsInvalidate } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/useOrganizationsInvalidate';
import { useOwnedAccount } from '@hotjar/account';
import type { Organization } from '@hotjar/organization';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useModalEvents } from '../../useModalEvents';
import { Event, UiPosition } from '../../events';
import * as api from './api';

const FORM_ID = 'edit-organization';

interface Props {
  handleSuccess(): void;
  handleCancel(): void;
  opened: boolean;
  organization: Pick<Organization, 'id' | 'name' | 'accountId'>;
}

const TRANSLATION_DOMAIN = 'account:delete-organization';

export function DeleteOrganizationModal({
  handleSuccess,
  handleCancel,
  opened,
  organization,
}: Props): React.ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { data: ownedAccountData } = useOwnedAccount();
  const accountId = ownedAccountData && ownedAccountData.id ? ownedAccountData.id : null;
  const invalidate = useOrganizationsInvalidate();
  const { showToast } = useNotify();
  const { trackClicked, trackOpened } = useModalEvents({
    organizationId: organization.id,
    accountId: organization.accountId,
    confirm: Event.CONFIRM_DELETE_ORGANIZATION_MODAL,
    uiPosition: UiPosition.DELETE_ORGANIZATION_MODAL,
    opened: Event.VIEW_DELETE_ORGANIZATION_MODAL,
  });

  const mutation = useMutation(
    () => {
      if (accountId !== null) {
        return api.deleteOrganization(accountId, organization.id);
      }
      return Promise.reject();
    },
    {
      onSuccess: () => {
        showToast({
          text: t('delete-success'),
          kind: 'info',
        });
        invalidate();
        handleSuccess();
      },
      onError: () => {
        showToast({ text: t('delete-failure'), kind: 'danger' });
      },
    },
  );

  useEffect(() => {
    trackOpened(opened);
  }, [opened, trackOpened]);

  return (
    <Modal isOpen={opened} size="s" contentLabel={t('title')} onClose={() => handleCancel()}>
      <Modal.Header title={t('title')} />
      <Modal.Body>
        <Text as="p">{t('confirmation', { name: organization.name })}</Text>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={() => handleCancel()} isDisabled={mutation.isLoading}>
          {t('cancel')}
        </Button>
        <Button
          kind="danger"
          type="submit"
          form={FORM_ID}
          isLoading={mutation.isLoading}
          onClick={() => {
            trackClicked();
            mutation.mutate();
          }}
        >
          {t('delete')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
